import React, { useContext, useEffect, useState } from "react"
import usePartnerAccess, {
  useGetPartnerInviteStatus,
  useRemovePartnerAccess,
  useUserRole
} from "../../../partner-access/hooks/usePartnerAccess"
import { FormDatePicker, FormTextField } from "@carrotfertility/carotene-core-x"
import { Box, Button, Stack, Typography, dayjs, useTheme } from "@carrotfertility/carotene-core"
import { useSelector } from "react-redux"
import { useFormContext } from "react-hook-form"
import { InviteStatusResult, PartnerInviteStatus } from "#/components/partner-access/partnerAccessTypes"
import { SettingsPageDivider } from "#/pages/account/page"
import { useCurrentUser } from "#/components/context/user/UserContext"
import { getCarrotLite } from "#/redux/reducers/companyInfo"
import { getHeap } from "#/utils/heap"
import { SettingsContext } from "#/pages/account"
import { HttpStatusCodes } from "#/utils/HttpStatusCodes"
import PasswordSettings from "../PasswordSettings"
import { PartnerGenderIdentitySettings, PartnerSexSettings } from "../MemberPartnerPersonalSettings"
import { emailValidation } from "#/utils/Regexes"
import { useIntlValidator } from "#/utils/hooks/useIntlValidator"
import { EmailWithChip } from "#/components/EmailVerification/EmailAddressesAccountSettings"
import { FormattedMessage, useIntl } from "react-intl"
import { validatePartnerDOB } from "#/services/common-forms"
import { CommunicationSettings } from "../CommunicationSettings"
import PartnerAccessRemovalDialog from "./PartnerAccessRemovalDialog"

function PartnerDateOfBirth({ inviteData = null }: { inviteData?: InviteStatusResult }): JSX.Element {
  const intl = useIntl()

  function validate(dateOfBirthValue: dayjs.Dayjs): string | boolean {
    try {
      dateOfBirthValue?.toISOString()
    } catch (e) {
      return intl.formatMessage({ defaultMessage: "Please enter a valid date" })
    }

    if (inviteData && inviteData?.status !== PartnerInviteStatus.NONE && !dateOfBirthValue) {
      return intl.formatMessage({ defaultMessage: "Required" })
    }

    if (!dateOfBirthValue) return true

    const result = validatePartnerDOB(dateOfBirthValue)
    if (typeof result !== "boolean") {
      return intl.formatMessage(result)
    }
    return result
  }

  return (
    <FormDatePicker
      required={inviteData && inviteData?.status !== PartnerInviteStatus.NONE}
      controllerProps={{ rules: { validate } }}
      name="partnerDateOfBirth"
      label={intl.formatMessage({ defaultMessage: "Date of birth" })}
    />
  )
}

function PartnerEmail({ inviteData = null }: { inviteData?: InviteStatusResult }): JSX.Element {
  const { personalEmail, email } = useCurrentUser()
  const { fieldErrorFromServer } = useContext(SettingsContext)
  const { setError } = useFormContext()
  const intl = useIntl()
  const intlEmailValidation = useIntlValidator(emailValidation)

  useEffect(() => {
    if (
      fieldErrorFromServer?.response?.status === HttpStatusCodes.CONFLICT &&
      fieldErrorFromServer?.message.includes("Partner email already exists")
    ) {
      setError("partnerEmail", {
        message: intl.formatMessage({ defaultMessage: "That email address is already in use" })
      })
      getHeap().track("DuplicateEmail", { EventName: "Attempted to use non-unique email" })
    }
  }, [setError, fieldErrorFromServer, intl])

  function validate(partnerEmail: string) {
    if (!partnerEmail) {
      return true
    }
    if (partnerEmail && (partnerEmail === personalEmail || partnerEmail === email)) {
      getHeap().track("DuplicateEmail", { EventName: "Attempted to use non-unique email" })
      return intl.formatMessage({ defaultMessage: "That email address is already associated with your account." })
    }

    return intlEmailValidation(partnerEmail)
  }
  return (
    <FormTextField
      label={intl.formatMessage({ defaultMessage: "Email" })}
      name="partnerEmail"
      registerOptions={{ validate }}
      disabled={inviteData?.status === PartnerInviteStatus.ACTIVE}
      type="email"
    />
  )
}

function PartnerInformationSettingsBody(): JSX.Element {
  const theme = useTheme()
  const intl = useIntl()

  return (
    <Stack spacing={theme.spacing(theme.tokens.spacing.lg)}>
      <Typography variant="h2">
        <FormattedMessage defaultMessage="Partner information" />
      </Typography>
      <FormTextField name="partnerFirstName" label={intl.formatMessage({ defaultMessage: "First name" })} />
      <FormTextField name="partnerLastName" label={intl.formatMessage({ defaultMessage: "Last name" })} />
      <FormTextField
        name="partnerPreferredName"
        label={intl.formatMessage({ defaultMessage: "Preferred name" })}
        inputProps={{ maxLength: 200 }}
      />
      <PartnerDateOfBirth />
      <PartnerGenderIdentitySettings
        names={{ genderIdentity: "partnerGenderIdentity", genderIdentityOther: "partnerGenderIdentityOther" }}
      />
      <PartnerSexSettings name="partnerSex" />
      <PartnerEmail />
    </Stack>
  )
}

export function PartnerInformationSettingsPrompt({ onClick }: { onClick: () => void }): JSX.Element {
  const theme = useTheme()
  return (
    <Stack
      spacing={theme.spacing(theme.tokens.spacing.md)}
      bgcolor={(theme) => theme.palette.primary.light}
      borderRadius={(theme) => theme.tokens.borderRadius.md}
      padding={(theme) => theme.spacing(theme.tokens.spacing.xxl)}
    >
      <Typography variant="h6">
        <FormattedMessage defaultMessage="Are you on your journey with a partner?" />
      </Typography>
      <Typography>
        <FormattedMessage
          defaultMessage="Providing details about your partner helps us better support both of you. You can change this information from
        here anytime."
        />
      </Typography>
      <Box display="flex" justifyContent="flex-end">
        <Button id="add-partner-info" onClick={onClick}>
          <FormattedMessage defaultMessage="Add partner info" />
        </Button>
      </Box>
    </Stack>
  )
}

function PartnerAccessPartnerInfoSettings({ inviteData }: { inviteData?: InviteStatusResult }): JSX.Element {
  const theme = useTheme()
  const intl = useIntl()
  const inviteStatusIsActiveOrInvited = inviteData && inviteData?.status !== PartnerInviteStatus.NONE

  // @ts-expect-error TS(7006) FIXME: Parameter 'fieldValue' implicitly has an 'any' typ... Remove this comment to see the full error message
  function validate(fieldValue) {
    return inviteStatusIsActiveOrInvited && fieldValue === ""
      ? intl.formatMessage({ defaultMessage: "Required" })
      : true
  }

  return (
    <Stack spacing={theme.spacing(theme.tokens.spacing.lg)} id="partnerInformationSettings">
      <Typography variant="h2">
        <FormattedMessage defaultMessage="Partner information" />
      </Typography>
      <FormTextField
        required={inviteStatusIsActiveOrInvited}
        registerOptions={{ validate }}
        name="partnerFirstName"
        label={intl.formatMessage({ defaultMessage: "First name" })}
      />
      <FormTextField
        required={inviteStatusIsActiveOrInvited}
        registerOptions={{ validate }}
        name="partnerLastName"
        label={intl.formatMessage({ defaultMessage: "Last name" })}
      />
      <FormTextField
        name="partnerPreferredName"
        label={intl.formatMessage({ defaultMessage: "Preferred name" })}
        inputProps={{ maxLength: 200 }}
      />
      <PartnerDateOfBirth {...{ inviteData }} />
      <PartnerGenderIdentitySettings
        names={{ genderIdentity: "partnerGenderIdentity", genderIdentityOther: "partnerGenderIdentityOther" }}
      />
      <PartnerSexSettings name="partnerSex" />
      <PartnerEmail {...{ inviteData }} />
    </Stack>
  )
}

// When in partner view
function PartnerAccessPartnerViewSettings(): JSX.Element {
  const { partnerEmail } = useCurrentUser()
  const theme = useTheme()
  const intl = useIntl()
  return (
    <>
      <SettingsPageDivider />
      <Stack spacing={theme.spacing(theme.tokens.spacing.lg)}>
        <Typography variant="h2">
          <FormattedMessage defaultMessage="Partner email address" />
        </Typography>
        <EmailWithChip
          email={partnerEmail}
          chipLabel={intl.formatMessage({
            defaultMessage: "partner sign-in"
          })}
        />
      </Stack>
      <SettingsPageDivider />
      <PasswordSettings isPartnerSection />
      <SettingsPageDivider />
      <CommunicationSettings isForPartner={true} />
    </>
  )
}

function PartnerAccessInformationActive({ inviteData }: { inviteData?: InviteStatusResult }): JSX.Element {
  const isCarrotLite = useSelector(getCarrotLite)
  const { partnerEmail } = useCurrentUser()
  const { isPartnerAccessingAccount } = useUserRole()
  const theme = useTheme()
  const intl = useIntl()
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const useRemovePartnerAccessQuery = useRemovePartnerAccess()

  const handleConfirmRemove = () => {
    useRemovePartnerAccessQuery.mutate(undefined, {
      onSuccess: () => {
        setIsDialogOpen(false)
      }
    })
  }

  return inviteData?.status === PartnerInviteStatus.ACTIVE && !isPartnerAccessingAccount ? (
    <>
      <SettingsPageDivider />
      <Typography variant="h2" marginBottom={(theme) => theme.spacing(theme.tokens.spacing.xs)}>
        <FormattedMessage defaultMessage="Partner email address" />
      </Typography>
      <Stack spacing={theme.spacing(theme.tokens.spacing.md)}>
        <Typography>
          {!isCarrotLite
            ? intl.formatMessage({
                defaultMessage:
                  "Your partner can sign in to this account with their own email address and password. They can access educational resources, benefit details, and more, but they won't be able to edit your account information, access financial areas, or see your in-app messages. They may be able to see personal information like gender identity."
              })
            : intl.formatMessage({
                defaultMessage:
                  "Your partner can sign in to this account with their own email address and password. They can access educational resources, benefit details, and more, but they won't be able to edit your account information or see your in-app messages. They may be able to see personal information like gender identity."
              })}
        </Typography>
        <EmailWithChip
          email={partnerEmail}
          chipLabel={intl.formatMessage({
            defaultMessage: "partner sign-in"
          })}
        />
        <>
          <Typography fontStyle="italic">
            <FormattedMessage defaultMessage="Need to change your partner's sign-in? Remove their current access, update their email address under Partner information and invite them again using their new email address." />
          </Typography>
          <Button
            id="remove-partner-access"
            sx={{ inlineSize: "fit-content" }}
            variant="outlined"
            color="secondary"
            onClick={() => setIsDialogOpen(true)}
          >
            <FormattedMessage defaultMessage="Revoke partner access" />
          </Button>
        </>
      </Stack>

      <PartnerAccessRemovalDialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onConfirmRemove={handleConfirmRemove}
        isLoading={useRemovePartnerAccessQuery.isLoading}
      />
    </>
  ) : null
}

function PartnerAccessPartnerInfoSettingsContainer(): JSX.Element {
  const { inviteData } = useGetPartnerInviteStatus()
  const { isPartnerAccessingAccount } = useUserRole()

  return (
    <>
      <PartnerAccessPartnerInfoSettings inviteData={inviteData} />
      {isPartnerAccessingAccount && <PartnerAccessPartnerViewSettings />}
      {!isPartnerAccessingAccount && <PartnerAccessInformationActive inviteData={inviteData} />}
    </>
  )
}

export function PartnerInformationSettings(): JSX.Element {
  const { showPartnerAccess } = usePartnerAccess()

  return !showPartnerAccess ? <PartnerInformationSettingsBody /> : <PartnerAccessPartnerInfoSettingsContainer />
}
